import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-bold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer active:scale-95',
  {
    variants: {
      variant: {
        default: 'bg-[#4a7c5e] text-white hover:bg-[#3b634b] focus-visible:ring-[#4a7c5e] shadow-lg shadow-[#4a7c5e]/20',
        destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        outline: 'border-2 border-slate-200 bg-white/50 backdrop-blur-sm text-slate-800 hover:border-[#4a7c5e] hover:bg-slate-50 focus-visible:ring-[#4a7c5e]',
        secondary: 'bg-[#c9903a] text-white hover:bg-[#b07d32] focus-visible:ring-[#c9903a]',
        ghost: 'text-slate-800 hover:bg-slate-100 focus-visible:ring-emerald-500',
        link: 'text-emerald-400 underline-offset-4 hover:underline hover:text-emerald-300 focus-visible:ring-emerald-500',
        premium: 'bg-slate-900 text-white hover:bg-slate-800 shadow-xl shadow-slate-900/10',
        glass: 'bg-white/40 backdrop-blur-xl border border-white/40 text-slate-800 hover:bg-white/60 shadow-lg shadow-black/5',
      },
      size: {
        default: 'h-12 px-6 py-3 text-lg rounded-2xl',
        sm: 'h-10 px-4 py-2 text-base rounded-xl',
        lg: 'h-14 px-8 py-4 text-xl rounded-2xl',
        xl: 'h-16 px-10 py-5 text-2xl rounded-[1.5rem]',
        icon: 'h-12 w-12 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
